import { isEnvironment } from './types';
export const getEnvFromHostname = (hostname) => {
    const DEVBOX_REGEXP_ON_DEV = /.*\.mfd-devbox-.+\.cloudbeds-dev.com/;
    const DEVBOX_REGEXP_ON_STAGE = /.*\.mfd-devbox-.+\.cloudbeds-stage.com/;
    const LOCAL_REGEXP = /.*\.cloudbeds-local.com/;
    const STAGE_REGEXP = /.*\.cloudbeds-stage.com/;
    if (DEVBOX_REGEXP_ON_DEV.test(hostname) ||
        DEVBOX_REGEXP_ON_STAGE.test(hostname))
        return 'devbox';
    if (LOCAL_REGEXP.test(hostname))
        return 'local';
    if (STAGE_REGEXP.test(hostname))
        return 'stage';
    return 'prod';
};
const getEnvFromParameters = (currentScriptSrc) => {
    if (currentScriptSrc) {
        const currentScriptURL = new URL(currentScriptSrc);
        const env = currentScriptURL.searchParams.get('env');
        if (env && isEnvironment(env)) {
            return env;
        }
    }
    return undefined;
};
export const getEnvironment = (system, hostname, entryPoint) => {
    return (system.environment ||
        getEnvFromParameters(entryPoint) ||
        getEnvFromHostname(hostname));
};
export const getIslandFromHostname = (hostname) => {
    if (hostname && hostname.indexOf('.cloudbeds') !== -1) {
        const regex = /([^]*?).cloudbeds/;
        const m = hostname.match(regex);
        return m && m.length > 0 ? m[1] || 'hotels' : 'hotels';
    }
    return 'hotels';
};
const getIslandFromParameters = (currentScriptSrc) => {
    if (currentScriptSrc) {
        const currentScriptURL = new URL(currentScriptSrc);
        const island = currentScriptURL.searchParams.get('island');
        if (island)
            return island;
    }
    return undefined;
};
const isValidIsland = (island) => !!island && island !== 'undefined';
export const getIsland = (system, hostname, entryPoint) => {
    if (isValidIsland(system.island)) {
        return system.island;
    }
    const paramIsland = getIslandFromParameters(entryPoint);
    if (isValidIsland(paramIsland)) {
        return paramIsland;
    }
    return getIslandFromHostname(hostname);
};
export const getLocalizationKey = (lng) => {
    const crowdinLangCodeMap = {
        cn: 'zh',
        'es-es': 'es',
        'pt-br': 'pt',
        'pt-pt': 'pt',
        ua: 'uk',
        vn: 'vi',
    };
    return crowdinLangCodeMap[lng] || lng;
};
/**
 * Returns the <script> element that bootstrapped the app.
 *
 * For the classic-script builds (the immersive webpack bundle) this is
 * `document.currentScript`. For the hosted Vite build the entry ships as an ES
 * module, for which `document.currentScript` is always `null`, so we locate the
 * script the loader injected for the hosted bundle by its stable, unhashed
 * filename (`static/js/main.js`). Its `data-*`/nonce are propagated onto it by
 * loader.js. Deliberately avoids `import.meta` so this module stays valid under
 * the CommonJS transforms used by Jest and the Cucumber/jsdom suite.
 */
export const getEntryScript = () => {
    const current = document.currentScript;
    if (current)
        return current;
    // Match without a leading slash so it works whether the loader injects an
    // absolute CDN URL (…/static/js/main.js) or a relative one (static/js/main.js).
    return document.querySelector('script[src*="static/js/main.js"]');
};
/**
 * Collects all attributes from the current script tag.
 * Sources (in priority order):
 * 1. URL search params from script src
 * 2. HTML element attributes (excluding standard script attributes)
 * 3. data-* attributes (via dataset)
 *
 * @returns Record with camelCase keys and string values
 */
export const getScriptAttributes = () => {
    const currentScript = getEntryScript();
    if (!currentScript)
        return {};
    const attributes = {};
    const toCamelCase = (key) => key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    // Collect search params from script src URL
    try {
        const scriptUrl = new URL(currentScript.src);
        scriptUrl.searchParams.forEach((value, key) => {
            const camelKey = toCamelCase(key);
            attributes[camelKey] = value;
        });
    }
    catch (_a) {
        // Inline script or invalid URL, ignore search params
    }
    // Collect element attributes (excluding standard script attributes)
    const excludedAttributes = new Set([
        'src',
        'type',
        'async',
        'defer',
        'crossorigin',
        'integrity',
        'nomodule',
        'referrerpolicy',
    ]);
    for (const attr of Array.from(currentScript.attributes)) {
        const name = attr.name;
        if (!name.startsWith('data-') && !excludedAttributes.has(name)) {
            attributes[toCamelCase(name)] = attr.value;
        }
    }
    // Collect dataset (data-* already in camelCase)
    const dataset = currentScript.dataset;
    for (const key in dataset) {
        const value = dataset[key];
        if (value !== undefined) {
            attributes[key] = value;
        }
    }
    return attributes;
};
